/**
 * B2 — Anzeigemodell für einen Schreib-Vorschlag des Assistenten.
 *
 * Bis zum 09.09.2026 zeigte die folgenreichste Karte der App zwei Knöpfe und sonst nichts:
 * kein Umfang, keine Abteilungen, kein Zeitraum. Dabei liegt alles im Frontend bereit —
 * `ChatbotProposal.resolvedArguments` kommt seit jeher mit und wurde weggeworfen.
 *
 * Diese Datei macht daraus anzeigbare Felder. Sie ist eine **reine Funktion** und nimmt die
 * Sprache als Parameter (Muster: `modules/tasks/util/task-date-format.ts`) — nie
 * `navigator.language`, nie `undefined`.
 *
 * ---
 *
 * ## Die zwei Regeln, an denen diese Karte hängt
 *
 * **1 · Technische Schlüssel werden nie gezeigt.** Die Werkzeuge führen durchgängig Paare:
 * `employeeId` **und** `employeeLabel`, `departmentId` **und** `departmentLabel`,
 * `skillId`/`skillLabel`, `employeeIds`/`employeeLabelList`. Die Id-Hälfte ist für die
 * Ausführung da, die Label-Hälfte für Menschen. Eine Vorschau, in der „departmentId: 4"
 * steht, ist schlimmer als keine Vorschau — sie sieht nach Fehler aus. {@link isTechnicalKey}
 * blendet alles aus, was auf Id/Ids endet, plus eine kurze namentliche Liste.
 *
 * **2 · Ohne Label kein Feld.** Ein Argument erscheint nur, wenn es unter
 * `HELP_CHAT.FIELD.<schlüssel>` eine Übersetzung gibt. Fehlt sie, fällt das Feld weg statt
 * mit rohem Feldnamen aufzutauchen. Das ist gleichzeitig die Bremse gegen künftige
 * Backend-Schlüssel, die hier niemand kennt: Sie tauchen einfach nicht auf.
 *
 * ---
 *
 * ## Datenlage (am Backend nachgezählt, 09.09.2026)
 *
 * 35 Werkzeuge liefern einen `ProposalResult`; sie verwenden zusammen rund 110 verschiedene
 * Argument-Schlüssel. Die Titel stehen unter `HELP_CHAT.TOOL.<toolName>`; für ein Werkzeug
 * ohne gepflegten Titel bleibt `proposal.summary` — der Backend-Satz ist immer da.
 *
 * ## Verhältnis zum Trockenlauf (Vertragspunkt 3)
 *
 * Liefert das Backend ein anzeigefertiges `preview`, gewinnt das: Es weiss mehr als wir.
 * Kommen dort aber rohe Feldnamen an, ist das ein Backend-Fehler und kein Anlass, sie zu
 * zeigen — {@link looksDisplayReady} sortiert solche Lieferungen aus und wir nehmen
 * wieder das eigene Modell.
 */
import { ChatField, ChatbotProposal } from '../models/help-chat.models';
import { resolveTimeFormat } from 'src/app/core/services/language/time-format.util';

/** Was die Karte anzeigt: ein Titel und die Felder darunter. */
export interface ProposalPreview {
  /** Bereits übersetzter Titel (Werkzeugname), sonst die Backend-Zusammenfassung. */
  title: string;
  /** Anzeigefertige Felder. Kann leer sein — dann zeigt die Karte nur Titel und Knöpfe. */
  fields: ChatField[];
}

/**
 * Übersetzungsfunktion als Parameter statt `TranslateService` als Abhängigkeit — so bleibt
 * die Datei rein testbar und ohne Angular-Kontext benutzbar.
 *
 * `has` muss zurückgeben, ob ein Schlüssel WIRKLICH existiert: `translate.instant()` liefert
 * bei einem unbekannten Schlüssel den Schlüssel selbst zurück, und genau das würde sonst als
 * Feldbeschriftung auf dem Bildschirm landen.
 */
export interface PreviewI18n {
  instant(key: string, params?: Record<string, unknown>): string;
  has(key: string): boolean;
}

const FIELD_PREFIX = 'HELP_CHAT.FIELD.';
const TOOL_PREFIX = 'HELP_CHAT.TOOL.';

/**
 * Schlüssel, die niemandem etwas sagen.
 *
 * Neben der Endungsregel (Id/Ids) stehen hier die namentlichen Ausnahmen: `coexistingPending`
 * ist ein internes Flag der Abwesenheitsprüfung, `publishIds`/`eventIds` sind Id-Listen ohne
 * Id-Endung im Singular.
 */
const TECHNICAL_KEYS = new Set([
  'coexistingPending',
  'publishIds',
  'eventIds',
  'alertIds',
  'absenceIds',
  'requirementEventIds',
]);

/** Endet der Schlüssel auf eine Id oder eine Id-Liste? */
function isTechnicalKey(key: string): boolean {
  if (TECHNICAL_KEYS.has(key)) return true;
  return /(^|[a-z])Ids?$/.test(key) || key === 'id' || key === 'ids';
}

/**
 * Höchstzahl der Felder in einer Karte.
 *
 * Nicht Geschmack, sondern Platz: Auf 390px passt die Karte mit fünf Feldern samt
 * gestapelter Fußzeile auf einen Bildschirm. Wer sieben Felder zeigt, zwingt zum Scrollen,
 * bevor der Bestätigen-Knopf sichtbar wird — bei einer Schreibaktion die falsche Reihenfolge.
 */
const MAX_FIELDS = 5;

/** Erkennt einen reinen Tag (`2026-09-14`). */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Erkennt einen Zeitstempel (`2026-09-14T08:00:00`, optional mit Zone). */
const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
/** Erkennt eine reine Uhrzeit (`08:00`, `08:00:00`). */
const TIME_ONLY_RE = /^\d{1,2}:\d{2}(:\d{2})?$/;

/**
 * Baut das Anzeigemodell.
 *
 * @param proposal Der Vorschlag, wie er vom Backend kam.
 * @param i18n     Übersetzungszugriff (siehe {@link PreviewI18n}).
 * @param lang     App-Sprache aus dem `LanguageService` — Pflicht, kein Default aus dem Browser.
 */
export function buildProposalPreview(
  proposal: ChatbotProposal,
  i18n: PreviewI18n,
  lang: string,
): ProposalPreview {
  return {
    title: resolveToolTitle(proposal, i18n),
    fields: resolveFields(proposal, i18n, lang),
  };
}

/** `HELP_CHAT.TOOL.<name>`, sonst der Satz des Backends. */
function resolveToolTitle(proposal: ChatbotProposal, i18n: PreviewI18n): string {
  const key = TOOL_PREFIX + proposal.toolName;
  if (proposal.toolName && i18n.has(key)) return i18n.instant(key);
  return proposal.summary || '';
}

/**
 * Feldliste: Trockenlauf des Backends, sonst das eigene Modell — und in beiden Fällen
 * am Ende die drei Trockenlauf-Zusatzfelder, sofern geliefert.
 */
function resolveFields(
  proposal: ChatbotProposal,
  i18n: PreviewI18n,
  lang: string,
): ChatField[] {
  const base = looksDisplayReady(proposal.preview, i18n)
    ? proposal.preview!.slice(0, MAX_FIELDS)
    : fieldsFromArguments(proposal.resolvedArguments, i18n, lang);

  return [...base, ...dryRunFields(proposal, i18n)].slice(0, MAX_FIELDS);
}

/**
 * Prüft, ob ein geliefertes `preview` wirklich anzeigefertig ist.
 *
 * Der Vertrag sagt „anzeigefertig"; kommen dort trotzdem rohe Feldnamen an (`strategyId`,
 * `fromUtc`), ist das ein Backend-Fehler. Wir zeigen sie dann nicht, sondern fallen auf das
 * eigene Modell zurück — sichtbar falsch ist schlimmer als still ersetzt.
 *
 * Heuristik: eine Beschriftung in camelCase ohne Leerzeichen ist keine Beschriftung.
 */
export function looksDisplayReady(
  preview: ChatField[] | undefined,
  _i18n: PreviewI18n,
): boolean {
  if (!preview?.length) return false;
  return preview.every((f) => {
    const label = (f?.label ?? '').trim();
    if (!label) return false;
    const rohWirkend = /^[a-z][A-Za-z0-9]*$/.test(label) && !label.includes(' ');
    return !rohWirkend;
  });
}

/** Die drei additiven Trockenlauf-Felder — jedes nur, wenn wirklich geliefert. */
function dryRunFields(proposal: ChatbotProposal, i18n: PreviewI18n): ChatField[] {
  const out: ChatField[] = [];
  // ⚠️ Ohne echten Trockenlauf KEIN „Betroffen"-Feld. Eine geratene Zahl vor einer
  // Schreibaktion ist eine Zusage, die das System nicht halten kann.
  if (typeof proposal.affectedCount === 'number') {
    const einheit = (proposal.affectedLabel ?? '').trim();
    out.push({
      label: i18n.instant('HELP_CHAT.FIELD.affected'),
      value: einheit ? `${proposal.affectedCount} ${einheit}` : String(proposal.affectedCount),
    });
  }
  if (proposal.resultState) {
    out.push({
      label: i18n.instant('HELP_CHAT.FIELD.resultState'),
      value: proposal.resultState,
    });
  }
  return out;
}

/** Das eigene Modell: aus `resolvedArguments` werden Felder. */
function fieldsFromArguments(
  args: Record<string, unknown> | undefined,
  i18n: PreviewI18n,
  lang: string,
): ChatField[] {
  if (!args) return [];
  const out: ChatField[] = [];
  for (const [key, raw] of Object.entries(args)) {
    if (isTechnicalKey(key)) continue;
    const labelKey = FIELD_PREFIX + key;
    // Regel 2: ohne gepflegte Beschriftung kein Feld.
    if (!i18n.has(labelKey)) continue;
    const value = formatValue(raw, i18n, lang);
    if (!value) continue;
    out.push({ label: i18n.instant(labelKey), value });
    if (out.length >= MAX_FIELDS) break;
  }
  return out;
}

/**
 * Wert → Anzeigetext. Leerer String heisst „nichts zu zeigen", das Feld entfällt dann.
 *
 * Verschachtelte Objekte werden bewusst NICHT aufgefaltet: Was dort steht, ist
 * Werkzeug-Interna, und `[object Object]` auf einer Bestätigungskarte ist der schlechteste
 * aller Ausgänge.
 */
function formatValue(raw: unknown, i18n: PreviewI18n, lang: string): string {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'boolean') {
    return i18n.instant(raw ? 'HELP_CHAT.FIELD.VALUE_YES' : 'HELP_CHAT.FIELD.VALUE_NO');
  }
  if (typeof raw === 'number') return String(raw);
  if (Array.isArray(raw)) return formatList(raw, i18n, lang);
  if (typeof raw === 'object') return '';

  const text = String(raw).trim();
  if (!text) return '';
  if (DATE_TIME_RE.test(text)) return formatDateTime(text, lang);
  if (DATE_ONLY_RE.test(text)) return formatDate(text, lang);
  if (TIME_ONLY_RE.test(text)) return text.slice(0, 5);
  return translateEnum(text, i18n);
}

/**
 * Übersetzt einen Enum-Wert, falls dafür eine Übersetzung gepflegt ist.
 *
 * Am Gerät gemessen (09.09.2026): Die Karte zeigte „Strategie **Balanced**" — ein roher
 * `OptimizationStrategy`-Wert, englisch, in einer deutschen Oberfläche. Die Werte kommen
 * als `strategy.ToString()` aus dem Backend; wo das Backend sein eigenes `preview` liefert,
 * übersetzt es sie selbst (`StrategyDe()`), aber unser Rückfall über `resolvedArguments`
 * sieht nur das Enum.
 *
 * Dieselbe Bremse wie bei den Feldbeschriftungen: **kein Eintrag, kein Ersatz.** Ein Wert
 * ohne Übersetzung bleibt stehen, wie er ist — eine erfundene Eindeutschung wäre schlimmer
 * als ein englisches Wort.
 */
function translateEnum(text: string, i18n: PreviewI18n): string {
  // Nur was wie ein Enum aussieht: ein Wort, PascalCase, keine Ziffern, keine Leerzeichen.
  if (!/^[A-Z][A-Za-z]{2,}$/.test(text)) return text;
  const key = `HELP_CHAT.FIELD_VALUE.${text}`;
  return i18n.has(key) ? i18n.instant(key) : text;
}

/**
 * Liste → „Anna, Bert, Cem + 4 weitere".
 *
 * Die Kappung bei drei ist dieselbe Überlegung wie {@link MAX_FIELDS}: Eine Karte mit
 * vierzehn Namen schiebt den Bestätigen-Knopf aus dem Bild.
 */
function formatList(items: unknown[], i18n: PreviewI18n, lang: string): string {
  const texte = items
    .map((i) => formatValue(i, i18n, lang))
    .filter((t) => t.length > 0);
  if (!texte.length) return '';
  if (texte.length <= 3) return texte.join(', ');
  return i18n.instant('HELP_CHAT.FIELD.LIST_MORE', {
    items: texte.slice(0, 3).join(', '),
    count: texte.length - 3,
  });
}

/**
 * `yyyy-MM-dd` als LOKALEN Tag lesen.
 *
 * `new Date('2026-09-14')` ist Mitternacht UTC und damit westlich von Greenwich der 13. —
 * derselbe Off-by-one, gegen den `toDate()` im Aufgaben-Modul geschrieben wurde.
 */
function parseIso(text: string): Date | null {
  const nurTag = DATE_ONLY_RE.exec(text);
  const d = nurTag
    ? new Date(+text.slice(0, 4), +text.slice(5, 7) - 1, +text.slice(8, 10))
    : new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(text: string, lang: string): string {
  const d = parseIso(text);
  if (!d) return text;
  return new Intl.DateTimeFormat(lang || 'de', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

function formatDateTime(text: string, lang: string): string {
  const d = parseIso(text);
  if (!d) return text;
  return new Intl.DateTimeFormat(lang || 'de', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: resolveTimeFormat(lang || 'de') === 12,
  }).format(d);
}
